import {
  addCreatedMarkers,
  applyNoteChanges,
  applyTypeChanges,
  dropRemovedTypes,
  editMarkers,
  groupLabelByElementIdOf,
  moveMarkers,
  removeDeletedMarkers,
} from "./planPatches";
import {
  addCreatedRoutes,
  applyRouteTypeChanges,
  editRoutes,
  moveRoutes,
  removeDeletedRoutes,
} from "./routePatches";
import { applyPageChanges } from "./pagePatches";
import { routeGroupLabelOf } from "components/SmartSurveyor/shared/routeGroupLabelOf";
import type { PlanV3 } from "../../../utils/types";
import type {
  ElementTypeChange,
  HistoryRowChange,
  RouteTypeChange,
} from "../../types";
import type {
  ApplyHistoryChangesToPlan,
  PlanPatch,
  PlanChanges,
} from "./types";

// Removals first so a type restored under a reused name finds its label free, additions after.
const PLAN_PATCHES: PlanPatch[] = [
  removeDeletedMarkers,
  moveMarkers,
  addCreatedMarkers,
  editMarkers,
  applyNoteChanges,
  removeDeletedRoutes,
  moveRoutes,
  addCreatedRoutes,
  editRoutes,
  applyPageChanges,
  dropRemovedTypes,
  applyTypeChanges,
  applyRouteTypeChanges,
];

// Approvals are not part of the plan; useApprovalStatusV3 applies them. Tray verdicts live in the cable tab; diagram rows belong to the diagram.
const planChangesOf = (changes: HistoryRowChange[]): PlanChanges => {
  const planChanges: PlanChanges = {
    pages: [],
    markers: [],
    notes: [],
    elementTypes: [],
    routes: [],
    routeTypes: [],
  };
  for (const change of changes) {
    switch (change.table) {
      case "floorPlanPages": {
        planChanges.pages.push(change);
        break;
      }
      case "elementMarkers": {
        planChanges.markers.push(change);
        break;
      }
      case "elementMarkerComments": {
        planChanges.notes.push(change);
        break;
      }
      case "elementTypes": {
        planChanges.elementTypes.push(change);
        break;
      }
      case "routes": {
        planChanges.routes.push(change);
        break;
      }
      case "routeTypes": {
        planChanges.routeTypes.push(change);
        break;
      }
      // A thread row carries nothing the plan shows; its comment rows carry the note.
      case "elementMarkerCommentThreads":
      case "approvals":
      case "cableTrayVerdicts":
      case "diagramItemTypes":
      case "diagramItemOccurrences":
      case "itemTypeSettings":
      case "itemTypeMaterialPrices":
      case "itemTypeInstallationPrices": {
        break;
      }
    }
  }
  return planChanges;
};

const labelByTypeIdOf = (
  plan: PlanV3,
  elementTypes: ElementTypeChange[],
): ReadonlyMap<number, string> => {
  const labels = new Map<number, string>();
  for (const [label, meta] of Object.entries(plan.groupMeta)) {
    labels.set(meta.element_type_id, label);
  }
  // The entry's own rows win: they name types the plan has not seen yet.
  for (const { rowId, before, after } of elementTypes) {
    const type = after ?? before;
    if (type !== null) labels.set(rowId, type.name);
  }
  return labels;
};

const routeLabelByTypeIdOf = (
  plan: PlanV3,
  routeTypes: RouteTypeChange[],
): ReadonlyMap<number, string> => {
  const labels = new Map<number, string>();
  for (const group of plan.routeGroups) {
    labels.set(group.route_type_id, group.label);
  }
  // The entry's own rows win: they name cable types the plan has not seen yet.
  for (const { rowId, before, after } of routeTypes) {
    const type = after ?? before;
    if (type !== null) labels.set(rowId, routeGroupLabelOf(type.cableType));
  }
  return labels;
};

const routeGroupLabelByRouteIdOf = (
  plan: PlanV3,
): ReadonlyMap<string, string> => {
  const labels = new Map<string, string>();
  for (const group of plan.routeGroups) {
    for (const route of group.routes) labels.set(route.id, group.label);
  }
  return labels;
};

export const applyHistoryChangesToPlan: ApplyHistoryChangesToPlan = ({
  plan,
  changes,
}) => {
  const planChanges = planChangesOf(changes);
  const context = {
    ...planChanges,
    labelByTypeId: labelByTypeIdOf(plan, planChanges.elementTypes),
    groupLabelByElementId: groupLabelByElementIdOf(plan),
    routeLabelByTypeId: routeLabelByTypeIdOf(plan, planChanges.routeTypes),
    routeGroupLabelByRouteId: routeGroupLabelByRouteIdOf(plan),
  };

  let next = plan;
  for (const patch of PLAN_PATCHES) {
    const result = patch({ ...context, plan: next });
    if (result === null) return { kind: "reload" };
    next = result;
  }
  return { kind: "patched", plan: next };
};
